import {
    AUDIO_CONTENT_HINTS,
    VIDEO_CONTENT_HINTS,
    applyContentHint,
    createAudioMixingProcess,
    createAudioStreamProcess,
    createGetDisplayMedia,
    createMedia,
    createMediaSignals,
    createVideoStreamProcess,
    isInitial,
} from '@pexip/media';
import {isSendingAudio, isSendingVideo} from '@pexip/infinity';
import type {AudioContentHint, MediaTrack} from '@pexip/media';
import {denoiseWasm} from '@pexip/denoise/urls';
import type {RenderEffects, VideoProcessor} from '@pexip/media-processor';
import {
    urls as mpUrls,
    createSegmenter,
    createVideoProcessor,
    getDefaultInputProcessAdaptor,
} from '@pexip/media-processor';
import {
    StreamQuality,
    createPreviewAudioInputHook,
    createPreviewAudioOutputHook,
    createPreviewControllerHook,
    createPreviewHook,
    createPreviewVideoInputHook,
    currentBrowserName,
    qualityToMediaConstraints,
    isScreenShareSupported,
} from '@pexip/media-components';
import type {
    InputConstraintSet,
    MediaDeviceInfoLike,
} from '@pexip/media-control';
import {
    interpretCurrentFacingMode,
    areMultipleFacingModeSupported,
    toMediaDeviceInfo,
} from '@pexip/media-control';
import {proxyWithLog} from '@pexip/utils';

import {config, defaultUserConfig, shouldEnableAudioTuneables} from '../config';
import {applyCallTypeSendDirectionChange} from '../utils/changeAudioVideoCallTypeDirection';
import {logger} from '../logger';
import {mediaSignals} from '../signals/Media.signals';
import {userCustomImageSignal} from '../signals/ImageStore.signals';
import {
    FFT_SIZE,
    UPDATE_FREQUENCY_HZ,
    SILENT_DETECTION_DURATION_S,
    VAD_THROTTLE_MS,
    PROCESSING_WIDTH,
    PROCESSING_HEIGHT,
    SETTINGS_PROCESSING_WIDTH,
    SETTINGS_PROCESSING_HEIGHT,
    FRAME_RATE,
    BACKGROUND_BLUR_AMOUNT,
    FOREGROUND_THRESHOLD,
    EDGE_BLUR_AMOUNT,
    RENDER_EFFECTS,
    BG_IMAGE_URL,
    MASK_COMBINE_RATIO,
    TIME_WAIT_FOR_MUTED_TRACK_RECOVERY_MS,
    BACKGROUND_THRESHOLD,
    DOWN_SAMPLE_FACTOR,
    LIGHT_WRAP_BLUR_AMOUNT,
    LIGHT_WRAP_EDGE_BAND,
    LIGHT_WRAP_INTENSITY,
    LIGHT_WRAP_TIGHTNESS,
    MORPH_DILATE_RADIUS_PX,
    MORPH_ERODE_RADIUS_PX,
    MORPH_PASS,
    SIGMA_RANGE,
    SIGMA_SPACE,
    PERSON_CENTER,
} from '../constants';
import {applicationConfig} from '../applicationConfig';
import {
    endPresentationSignal,
    presentationStreamSignal,
} from '../signals/Meeting.signals';
import type {VideoStreamTrackProcessorAPIs} from '../types';

import {imageStore} from './Image.service';

const audioProcessor =
    applicationConfig.audioProcessing &&
    createAudioStreamProcess({
        shouldEnable: () => applicationConfig.audioProcessing,
        denoiseParams: {
            wasmURL: denoiseWasm.href,
            workletModule: mpUrls.denoise().href,
        },
        fftSize: FFT_SIZE,
        analyzerUpdateFrequency: UPDATE_FREQUENCY_HZ,
        audioSignalDetectionDuration: SILENT_DETECTION_DURATION_S,
        throttleMs: VAD_THROTTLE_MS,
        onAudioSignalDetected: mediaSignals.onSilentDetected.emit,
        onVoiceActivityDetected: mediaSignals.onVAD.emit,
        signals: mediaSignals,
    });

const taskVisionURL = new URL(
    './assets/@mediapipe/tasks-vision/wasm/',
    document.baseURI,
);

const taskVisionBasePath = taskVisionURL.pathname;
const litertjsCoreBasePath = new URL(
    './assets/@litertjs/core/wasm/',
    document.baseURI,
).pathname;
const selfieSegmenterURL = new URL(
    './assets/@mediapipe/models/selfie_segmenter_landscape.tflite',
    document.baseURI,
);
const modelAsset = {
    path: selfieSegmenterURL.pathname,
    modelName: 'selfie' as const,
};
const delegate = () => config.get('videoProcessingDelegate');
const selfie = proxyWithLog(logger, 'MainSelfie')(
    createSegmenter(taskVisionBasePath, {
        modelAsset,
        delegate,
        litertjsCoreBasePath,
        log: {
            debug(meta, message) {
                logger.debug({context: 'media segmenter', meta: meta}, message);
            },
            info(meta, message) {
                logger.info({context: 'media segmenter', meta: meta}, message);
            },
            warn(meta, message) {
                logger.warn({context: 'media segmenter', meta: meta}, message);
            },
            error(meta, message) {
                logger.error({context: 'media segmenter', meta: meta}, message);
            },
        },
    }),
    'Segmenter',
);

/**
 * Subscribe the `ProcessorRestarted` signal to re-request the media if the track
 * is muted. On some weird cases, the processed track is muted after
 * `ProcessorRestarted` and never recovered.
 */
selfie.subscribe('ProcessorRestarted', () => {
    const startTime = performance.now();
    const handleTrackMuted = () => {
        mediaSignals.onMediaTrackResumed.remove(handleTrackUnmuted);
        const endTime = performance.now();
        const [track] = mediaService.media?.stream?.getVideoTracks() ?? [];
        if (track?.muted) {
            // The track is muted, so we need to re-request the media
            logger.debug(
                {track, muted: track.muted, startTime, endTime},
                'Track muted after ProcessorRestarted, re-requesting media',
            );
            mediaService.getUserMedia(getDefaultConstraints());
        }
    };
    const handleTrackUnmuted = () => {
        mediaSignals.onMediaTrackResumed.remove(handleTrackUnmuted);
        clearTimeout(mutedTrackTimer);
    };
    const mutedTrackTimer = setTimeout(
        handleTrackMuted,
        TIME_WAIT_FOR_MUTED_TRACK_RECOVERY_MS,
    );
    mediaSignals.onMediaTrackResumed.add(handleTrackUnmuted);
});

const chooseVideoProcessorAPI = (): VideoStreamTrackProcessorAPIs => {
    const videoProcessingAPI = config.get('videoProcessingAPI');
    if (videoProcessingAPI === 'stream' || videoProcessingAPI === 'canvas') {
        return videoProcessingAPI;
    }
    return 'stream';
};

export const browserSupportsPtzConstraints = () => {
    const browserSupports = navigator.mediaDevices.getSupportedConstraints();
    return (
        'pan' in browserSupports &&
        'tilt' in browserSupports &&
        'zoom' in browserSupports
    );
};

const renderParams = {
    backgroundBlurAmount: BACKGROUND_BLUR_AMOUNT,
    foregroundThreshold: FOREGROUND_THRESHOLD,
    backgroundThreshold: BACKGROUND_THRESHOLD,
    edgeBlurAmount: EDGE_BLUR_AMOUNT,
    videoSegmentation: RENDER_EFFECTS,
    maskCombineRatio: MASK_COMBINE_RATIO,
    backgroundImageUrl: BG_IMAGE_URL,
    downSampleFactor: DOWN_SAMPLE_FACTOR,
    lightWrapBlurAmount: LIGHT_WRAP_BLUR_AMOUNT,
    lightWrapEdgeBand: LIGHT_WRAP_EDGE_BAND,
    lightWrapIntensity: LIGHT_WRAP_INTENSITY,
    lightWrapTightness: LIGHT_WRAP_TIGHTNESS,
    morphDilateRadiusPx: MORPH_DILATE_RADIUS_PX,
    morphErodeRadiusPx: MORPH_ERODE_RADIUS_PX,
    morphPass: MORPH_PASS,
    sigmaRangeHi: SIGMA_RANGE[1],
    sigmaRangeLo: SIGMA_RANGE[0],
    sigmaSpace: SIGMA_SPACE,
    personCenterX: PERSON_CENTER[0],
    personCenterY: PERSON_CENTER[1],
};

let videoProcessor: VideoProcessor;

const getVideoProcessor = () => {
    if (videoProcessor) {
        return videoProcessor;
    }
    videoProcessor = createVideoProcessor(
        selfie,
        getDefaultInputProcessAdaptor(
            chooseVideoProcessorAPI() === 'stream',
            FRAME_RATE,
        ),
    );
    return videoProcessor;
};

const videoStreamProcessor =
    applicationConfig.videoProcessing &&
    createVideoStreamProcess({
        getVideoProcessor,
        dynamicProcessingDimensions: () =>
            applicationConfig.dynamicVideoProcessingDimensions,
        frameRate: FRAME_RATE,
        gpuAPI: () => config.get('gpuAPI'),
        referenceProcessingHeight: PROCESSING_HEIGHT,
        processingHeight: PROCESSING_HEIGHT,
        processingWidth: PROCESSING_WIDTH,
        shouldEnable: () => applicationConfig.videoProcessing,
        stopAsMute: () => config.get('stopCameraAsMute'),
        signals: mediaSignals,
        ...renderParams,
    });

export const getDisplayMedia = createGetDisplayMedia(() => ({
    video: {
        displaySurface: config.get('displaySurface'),
        cursor: config.get('cursorCapture'),
        // Set max capture resolution to screen sharing
        // Setting other hardcoded values could lead to too much resampling
        // Ref https://github.com/pexip/aquila/issues/2381
        width: {max: window.screen.width},
        height: {max: window.screen.height},
    },
    audio: config.get('captureAudio'),
    surfaceSwitching: config.get('surfaceSwitching'),
    monitorTypeSurfaces: applicationConfig.monitorTypeSurfaces,
    selfBrowserSurface: applicationConfig.selfBrowserSurface,
    systemAudio: applicationConfig.systemAudio,
}));

let currentDisplayMedia: MediaStream | undefined;
export const getCurrentDisplayMedia = () => currentDisplayMedia;

/**
 * Derive the audio content hint based on the presentation MediaStream and the
 * preference from the user.
 *
 * @param stream - The MediaStream for the presentation, e.g. what you get from
 * `getDisplayMedia`
 */
export const deriveAudioContentHintFromPreso = (
    stream: MediaStream | undefined,
) => {
    const contentHint =
        stream &&
        stream.getAudioTracks().length > 0 &&
        config.get('presoContentHint') === VIDEO_CONTENT_HINTS.Motion
            ? AUDIO_CONTENT_HINTS.Music
            : defaultUserConfig.audioContentHint;
    return contentHint;
};

/**
 * What audio features to turn on based on the audio content hint
 *
 * @param hint - Audio content hint
 * @returns audio constraints e.g. echoCancellation, autoGainControl, noiseSuppression & denoise.
 */
export const deriveAudioFeaturesFromAudioContentHint = (
    hint: AudioContentHint,
) => {
    switch (hint) {
        case 'speech': {
            return {
                echoCancellation: config.get('echoCancellation'),
                autoGainControl: config.get('autoGainControl'),
                noiseSuppression:
                    shouldEnableAudioTuneables() &&
                    (config.get('noiseSuppression') || !config.get('denoise')),
                denoise: config.get('denoise'),
            };
        }
        case 'music': {
            // For an audio track with the value "music", and for constraints
            // echoCancellation, autoGainControl and noiseSuppression apply a default of "false".
            // https://www.w3.org/TR/mst-content-hint/#behavior-of-a-mediastreamtrack
            return {
                echoCancellation: false,
                autoGainControl: false,
                noiseSuppression: false,
                denoise: false,
            };
        }
        default: {
            return {
                echoCancellation: config.get('echoCancellation'),
                autoGainControl: config.get('autoGainControl'),
                noiseSuppression:
                    shouldEnableAudioTuneables() &&
                    (config.get('noiseSuppression') || !config.get('denoise')),
                denoise: config.get('denoise'),
            };
        }
    }
};

export const setCurrentDisplayMedia = (newDisplayMedia?: MediaStream) => {
    currentDisplayMedia = newDisplayMedia;
    const [audioTrack] = newDisplayMedia?.getAudioTracks() ?? [];
    presentationStreamSignal.emit(newDisplayMedia);
    if (audioTrack) {
        newDisplayMedia
            ?.getVideoTracks()
            .forEach(applyContentHint(config.get('presoContentHint')));

        // if the presentation stream has attached audio, set contentHint to "music"
        const contentHint = deriveAudioContentHintFromPreso(newDisplayMedia);
        void mediaService.media?.applyConstraints({
            audio: {
                mixWithAdditionalMedia: true,
                ...deriveAudioFeaturesFromAudioContentHint(contentHint),
                contentHint,
            },
        });
    }
};
const presentationMixer = createAudioMixingProcess(
    getCurrentDisplayMedia,
    mediaSignals,
);

const getFacingMode = (isUserFacing: boolean) =>
    isUserFacing ? 'user' : 'environment';
const isUserFacingMode = (mode: string) => mode === 'user';

const hasValidDevice = (info: MediaDeviceInfoLike | undefined): boolean =>
    !!(info && 'deviceId' in info);

export const getDefaultConstraints = () => {
    const callType = config.get('callType');
    const requestAudio = isSendingAudio(callType);
    const requestVideo = isSendingVideo(callType);
    const audioDevice = config.get('audioInput');
    const videoDevice = config.get('videoInput');
    const audio: InputConstraintSet | false = requestAudio
        ? {
              sampleRate: 48000,
              echoCancellation: config.get('echoCancellation'),
              autoGainControl: config.get('autoGainControl'),
              noiseSuppression:
                  !config.get('denoise') && config.get('noiseSuppression'),
              denoise: config.get('denoise'),
              vad: config.get('vad'),
              asd: config.get('asd'),
              contentHint: config.get('audioContentHint'),
              mixWithAdditionalMedia: false,
              ...(hasValidDevice(audioDevice) ? {device: audioDevice} : {}),
          }
        : false;
    const fecc = config.get('fecc');
    const video: InputConstraintSet | false = requestVideo
        ? {
              ...qualityToMediaConstraints(getStreamQuality()),
              foregroundThreshold: config.get('foregroundThreshold'),
              backgroundBlurAmount: config.get('backgroundBlurAmount'),
              edgeBlurAmount: config.get('edgeBlurAmount'),
              maskCombineRatio: config.get('maskCombineRatio'),
              frameRate: applicationConfig.frameRate,
              videoSegmentation: config.get('segmentationEffects'),
              backgroundImageUrl: config.get('bgImageUrl'),
              facingMode: getFacingMode(config.get('isUserFacing')),
              resizeMode: 'none',
              contentHint: config.get('videoContentHint'),
              backgroundThreshold: config.get('backgroundThreshold'),
              downSampleFactor: config.get('downSampleFactor'),
              lightWrapBlurAmount: config.get('lightWrapBlurAmount'),
              lightWrapEdgeBand: config.get('lightWrapEdgeBand'),
              lightWrapIntensity: config.get('lightWrapIntensity'),
              lightWrapTightness: config.get('lightWrapTightness'),
              morphDilateRadiusPx: config.get('morphDilateRadiusPx'),
              morphErodeRadiusPx: config.get('morphErodeRadiusPx'),
              morphPass: config.get('morphPass'),
              sigmaRangeHi: config.get('sigmaRangeHi'),
              sigmaRangeLo: config.get('sigmaRangeLo'),
              sigmaSpace: config.get('sigmaSpace'),
              personCenterX: config.get('personCenterX'),
              personCenterY: config.get('personCenterY'),
              ...(hasValidDevice(videoDevice) ? {device: videoDevice} : {}),
              ...(!browserSupportsPtzConstraints()
                  ? {}
                  : {pan: fecc, tilt: fecc, zoom: fecc}),
          }
        : false;
    return {audio, video};
};

export const mediaService = createMedia({
    getMuteState: () => ({
        audio: config.get('isAudioInputMuted'),
        video: config.get('isVideoInputMuted'),
    }),
    signals: mediaSignals,
    stopVideoTrackAsMute: () => config.get('stopCameraAsMute'),
    audioProcessors: [
        audioProcessor,
        isScreenShareSupported && presentationMixer,
    ].flatMap(processor => (processor ? [processor] : [])),
    videoProcessors: [videoStreamProcessor].flatMap(processor =>
        processor ? [processor] : [],
    ),
    getDefaultConstraints,
});

const handleUserCustomImage: Parameters<
    typeof userCustomImageSignal.add
>[0] = record => {
    if (!record) {
        return;
    }
    // No need to wait
    void getVideoProcessor()
        .update({
            backgroundImage: imageStore.getBitmapRecord(),
        })
        .catch(error => {
            logger.error({error}, 'Failed to update custom image to main');
        });

    void getPreviewVideoProcessor()
        .update({
            backgroundImage: imageStore.getBitmapRecord(),
        })
        .catch(error => {
            logger.error({error}, 'Failed to update custom image to preview');
        });
};
userCustomImageSignal.add(handleUserCustomImage);

config.subscribe('backgroundBlurAmount', backgroundBlurAmount => {
    void mediaService.media?.applyConstraints({video: {backgroundBlurAmount}});
});
config.subscribe('foregroundThreshold', foregroundThreshold => {
    void mediaService.media?.applyConstraints({video: {foregroundThreshold}});
});
config.subscribe('backgroundThreshold', backgroundThreshold => {
    void mediaService.media?.applyConstraints({video: {backgroundThreshold}});
});

config.subscribe('maskCombineRatio', maskCombineRatio => {
    void mediaService.media?.applyConstraints({video: {maskCombineRatio}});
});

config.subscribe('downSampleFactor', downSampleFactor => {
    void mediaService.media?.applyConstraints({video: {downSampleFactor}});
});

config.subscribe('lightWrapBlurAmount', lightWrapBlurAmount => {
    void mediaService.media?.applyConstraints({video: {lightWrapBlurAmount}});
});

config.subscribe('lightWrapEdgeBand', lightWrapEdgeBand => {
    void mediaService.media?.applyConstraints({video: {lightWrapEdgeBand}});
});

config.subscribe('lightWrapIntensity', lightWrapIntensity => {
    void mediaService.media?.applyConstraints({video: {lightWrapIntensity}});
});

config.subscribe('lightWrapTightness', lightWrapTightness => {
    void mediaService.media?.applyConstraints({video: {lightWrapTightness}});
});

config.subscribe('morphDilateRadiusPx', morphDilateRadiusPx => {
    void mediaService.media?.applyConstraints({video: {morphDilateRadiusPx}});
});

config.subscribe('morphErodeRadiusPx', morphErodeRadiusPx => {
    void mediaService.media?.applyConstraints({video: {morphErodeRadiusPx}});
});

config.subscribe('morphPass', morphPass => {
    void mediaService.media?.applyConstraints({video: {morphPass}});
});

config.subscribe('sigmaRangeHi', sigmaRangeHi => {
    void mediaService.media?.applyConstraints({video: {sigmaRangeHi}});
});

config.subscribe('sigmaRangeLo', sigmaRangeLo => {
    void mediaService.media?.applyConstraints({video: {sigmaRangeLo}});
});

config.subscribe('sigmaSpace', sigmaSpace => {
    void mediaService.media?.applyConstraints({video: {sigmaSpace}});
});

config.subscribe('edgeBlurAmount', edgeBlurAmount => {
    void mediaService.media?.applyConstraints({video: {edgeBlurAmount}});
});
config.subscribe('bgImageUrl', backgroundImageUrl => {
    void mediaService.media?.applyConstraints({
        video: {backgroundImageUrl},
    });
});
config.subscribe('denoise', denoise => {
    void mediaService.media?.applyConstraints({
        audio: {denoise, noiseSuppression: !denoise},
    });
});
config.subscribe('vad', vad => {
    void mediaService.media?.applyConstraints({
        audio: {vad},
    });
});
config.subscribe('asd', asd => {
    void mediaService.media?.applyConstraints({
        audio: {asd},
    });
});
config.subscribe('fecc', () => {
    if (mediaService.media?.status && !isInitial(mediaService.media.status)) {
        mediaService.getUserMedia(getDefaultConstraints());
    }
});
config.subscribe('bandwidth', () => {
    void mediaService.media?.applyConstraints({
        video: {...qualityToMediaConstraints(getStreamQuality())},
    });
});
config.subscribe('presoContentHint', hint => {
    currentDisplayMedia?.getVideoTracks().forEach(applyContentHint(hint));
    const audioContentHint =
        deriveAudioContentHintFromPreso(currentDisplayMedia);
    void mediaService.media?.applyConstraints({
        audio: {
            ...deriveAudioFeaturesFromAudioContentHint(audioContentHint),
            contentHint: audioContentHint,
        },
    });
});

endPresentationSignal.add(async () => {
    const contentHint = deriveAudioContentHintFromPreso(undefined);
    await mediaService.media?.applyConstraints({
        audio: {
            mixWithAdditionalMedia: false,
            contentHint,
            ...deriveAudioFeaturesFromAudioContentHint(contentHint),
        },
    });
});

export const muteAudio = (muted: boolean, persist?: boolean) => {
    config.set({key: 'isAudioInputMuted', value: muted, persist});
};

config.subscribe('isAudioInputMuted', isMuted => {
    if (
        mediaService.media?.audioMuted !== undefined &&
        mediaService.media.audioMuted !== isMuted
    ) {
        mediaService.media?.muteAudio(isMuted);
    }
});
export const muteVideo = (muted: boolean, persist?: boolean) => {
    config.set({key: 'isVideoInputMuted', value: muted, persist});
};
config.subscribe('isVideoInputMuted', isMuted => {
    if (
        mediaService.media?.videoMuted !== undefined &&
        mediaService.media.videoMuted !== isMuted
    ) {
        mediaService.media?.muteVideo(isMuted);
    }
});

const logTrack = (event: string) => (track: MediaTrack) => {
    logger[mediaService.media?.isCurrentTrack(track) ? 'info' : 'debug'](
        {track},
        `[MediaTrack ${event} event] ${track.kind}[${track.label}|${track.id}] muted:${track.muted} suspended:${track.suspended}`,
    );
};

const logMuted = logTrack('muted');
const logSuspended = logTrack('suspended');
const logResumed = logTrack('resumed');
const logStopped = logTrack('stopped');

mediaSignals.onMediaTrackMuted.add(logMuted);
mediaSignals.onMediaTrackSuspended.add(logSuspended);
mediaSignals.onMediaTrackResumed.add(logResumed);
mediaSignals.onMediaTrackStopped.add(logStopped);

const previewSegmenter = proxyWithLog(logger, 'Preview Segmenter')(
    createSegmenter(taskVisionBasePath, {
        modelAsset,
        delegate,
        litertjsCoreBasePath,
        log: {
            debug(meta, message) {
                logger.debug(
                    {context: 'preview segmenter', meta: meta},
                    message,
                );
            },
            info(meta, message) {
                logger.info(
                    {context: 'preview segmenter', meta: meta},
                    message,
                );
            },
            warn(meta, message) {
                logger.warn(
                    {context: 'preview segmenter', meta: meta},
                    message,
                );
            },
            error(meta, message) {
                logger.error(
                    {context: 'preview segmenter', meta: meta},
                    message,
                );
            },
        },
    }),
    'Segmenter',
);

let previewVideoProcessor: VideoProcessor;

const getPreviewVideoProcessor = () => {
    if (previewVideoProcessor) {
        return previewVideoProcessor;
    }
    previewVideoProcessor = createVideoProcessor(
        previewSegmenter,
        getDefaultInputProcessAdaptor(
            chooseVideoProcessorAPI() === 'stream',
            FRAME_RATE,
        ),
    );
    return previewVideoProcessor;
};

export const usePreviewController = createPreviewControllerHook(() => {
    const previewSignals = createMediaSignals(
        [
            'onAddTrack',
            'onMediaTrackMuted',
            'onMediaTrackSuspended',
            'onMediaTrackResumed',
            'onRemoveTrack',
            'onStatusChanged',
            'onUpdatingAudio',
            'onUpdatingVideo',
        ],
        'PreviewStreamController',
    );
    const renderParams = {
        width: SETTINGS_PROCESSING_WIDTH,
        height: SETTINGS_PROCESSING_HEIGHT,
        effects: config.get('segmentationEffects'),
        frameRate: applicationConfig.frameRate,
        backgroundBlurAmount: config.get('backgroundBlurAmount'),
        foregroundThreshold: config.get('foregroundThreshold'),
        edgeBlurAmount: config.get('edgeBlurAmount'),
        backgroundImageUrl: config.get('bgImageUrl'),
        maskCombineRatio: config.get('maskCombineRatio'),
        backgroundImage: imageStore.getBitmapRecord(),
        backgroundThreshold: config.get('backgroundThreshold'),
        downSampleFactor: config.get('downSampleFactor'),
        lightWrapBlurAmount: config.get('lightWrapBlurAmount'),
        lightWrapEdgeBand: config.get('lightWrapEdgeBand'),
        lightWrapIntensity: config.get('lightWrapIntensity'),
        lightWrapTightness: config.get('lightWrapTightness'),
        morphDilateRadiusPx: config.get('morphDilateRadiusPx'),
        morphErodeRadiusPx: config.get('morphErodeRadiusPx'),
        morphPass: config.get('morphPass'),
        sigmaRangeHi: config.get('sigmaRangeHi'),
        sigmaRangeLo: config.get('sigmaRangeLo'),
        sigmaSpace: config.get('sigmaSpace'),
        personCenterX: config.get('personCenterX'),
        personCenterY: config.get('personCenterY'),
    };

    return {
        getCurrentDevices: () => mediaService.devices,
        getCurrentMedia: () => mediaService.media,
        getDefaultConstraints,
        updateMainStream: constraints => {
            applyCallTypeSendDirectionChange(constraints);
            return mediaService.getUserMediaAsync(constraints);
        },
        mainMediaSignal: mediaSignals.onMediaChanged,
        signals: previewSignals,
        audioProcessors: [],
        videoProcessors: [
            applicationConfig.videoProcessing &&
                createVideoStreamProcess({
                    getVideoProcessor: getPreviewVideoProcessor,
                    shouldEnable: () => applicationConfig.videoProcessing,
                    label: 'PreviewVideoProcessor',
                    ...renderParams,
                    referenceProcessingHeight: PROCESSING_HEIGHT,
                    processingWidth: renderParams.width,
                    processingHeight: renderParams.height,
                    videoSegmentation: renderParams.effects,
                    signals: previewSignals,
                    gpuAPI: () => config.get('gpuAPI'),
                }),
        ].flatMap(processor => (processor ? [processor] : [])),
        stopVideoTrackAsMute: () => config.get('stopCameraAsMute'),
    };
});

export const getStreamQuality = (bandwidth = config.get('bandwidth')) => {
    const [low, medium, high, veryHigh] = applicationConfig.bandwidths;

    switch (bandwidth) {
        case low:
            return StreamQuality.Low;
        case medium:
            return StreamQuality.Medium;
        case high:
            return StreamQuality.High;
        case veryHigh:
            return StreamQuality.VeryHigh;
        default:
            return StreamQuality.Auto;
    }
};

export const setStreamQuality = (streamQuality: StreamQuality) => {
    let value: string;
    const [low, medium, high, veryHigh] = applicationConfig.bandwidths;

    switch (streamQuality) {
        case StreamQuality.Low:
            value = low;
            break;
        case StreamQuality.Medium:
            value = medium;
            break;
        case StreamQuality.High:
            value = high;
            break;
        case StreamQuality.VeryHigh:
            value = veryHigh;
            break;
        default:
            value = '';
            break;
    }

    if (config.get('bandwidth') !== value) {
        config.set({key: 'bandwidth', value, persist: true});
        return true;
    }
    return false;
};

export const usePreviewStreamQuality = createPreviewHook({
    get: getStreamQuality,
    set: setStreamQuality,
});

export const usePreviewSegmentationEffects = createPreviewHook({
    get: () => config.get('segmentationEffects'),
    set: (value: RenderEffects) => {
        config.set({key: 'segmentationEffects', value, persist: true});
        return false;
    },
});

export const usePreviewBgImageUrl = createPreviewHook({
    get: () => config.get('bgImageUrl'),
    set: (value: string) => {
        config.set({key: 'bgImageUrl', value, persist: true});
        return false;
    },
});

export const usePreviewBackgroundBlurAmount = createPreviewHook({
    get: () => config.get('backgroundBlurAmount'),
    set: (value: number) => {
        config.set({key: 'backgroundBlurAmount', value, persist: true});
        return false;
    },
});

export const usePreviewAudioOutput = createPreviewAudioOutputHook(
    (value: MediaDeviceInfoLike) =>
        config.set({key: 'audioOutput', value, persist: true}),
);

export const usePreviewAudioInput = createPreviewAudioInputHook({
    get: () => mediaService.media?.audioInput,
    getExpected: () =>
        mediaService.media?.audioInput ??
        mediaService.media?.expectedAudioInput,
    set: (value?: MediaDeviceInfoLike) =>
        value &&
        config.set({
            key: 'audioInput',
            value: toMediaDeviceInfo(value),
            persist: true,
        }),
});

export const usePreviewVideoInput = createPreviewVideoInputHook({
    get: () => mediaService.media?.videoInput,
    getExpected: () =>
        mediaService.media?.videoInput ??
        mediaService.media?.expectedVideoInput,
    set: (value?: MediaDeviceInfoLike) =>
        value &&
        config.set({
            key: 'videoInput',
            value: toMediaDeviceInfo(value),
            persist: true,
        }),
});

export const enableAudioSignalDetection = () =>
    config.set({key: 'asd', value: true});

export const disableAudioSignalDetection = () =>
    config.set({key: 'asd', value: false});

let cacheFacingModeToggleDetected = false;
export const canShowFacingModeToggle = (
    devices: MediaDeviceInfoLike[],
): boolean => {
    if (cacheFacingModeToggleDetected) {
        return cacheFacingModeToggleDetected;
    }
    cacheFacingModeToggleDetected =
        areMultipleFacingModeSupported(devices) ||
        // Safari translates the device label which makes it unreliable to snoop the label
        currentBrowserName === 'Safari iPad' ||
        currentBrowserName === 'Safari iPhone';
    return cacheFacingModeToggleDetected;
};

export const toggleFacingMode = (track: MediaStreamTrack | undefined) => {
    // FIXME: you probably shouldn't set it in the first place but we do in express flow on mobile
    config.set({
        key: 'videoInput',
        value: undefined,
    });
    const currentFacingMode =
        interpretCurrentFacingMode(track) ??
        (config.get('isUserFacing') ? 'user' : 'environment');
    const isUserFacing = !isUserFacingMode(currentFacingMode);
    config.set({
        key: 'isUserFacing',
        value: isUserFacing,
        persist: true,
    });
    // Do not send a mute request to backend if the user is toggling the camera
    mediaService.getUserMedia({
        audio: true,
        video: {
            facingMode: {
                ideal: getFacingMode(isUserFacing),
            },
        },
    });
};
